class ShopifyHeader extends HTMLElement {
    constructor() {
        super()
        this.header = this
        this.lastScrollTop = 0
        this.ticking = false
    }

    connectedCallback() {
        this.menuType = this.dataset.menuType
        this.stickyBehavior = this.dataset.stickyBehavior
        this.mobileMenuType = this.dataset.mobileMenuType
        this.submenuAction = this.dataset.submenuAction
        this.megamenus = document.querySelectorAll("megamenu")
        this.pageTemplate = this.getAttribute("data-page-template")
        this.transparentTemplate =
            this.getAttribute("data-transparent-template") === "true"
                ? true
                : false
        this.transparentHeader = this.getAttribute("data-transparent-header")
        this.setupStickyHeader()

        if (this.menuType === "dropdown" || this.menuType === "mega") {
            this.setupDesktopSubmenuBehavior()
        }

        // this.setupMenuDrawer()
        this.setupPredictiveSearch()
        this.setupMegamenu()
    }

    setupStickyHeader() {
        this.observeHeights()

        // Theme editor re-renders the announcement bar section on edit
        document.addEventListener("shopify:section:load", () =>
            this.observeHeights()
        )
        document.addEventListener("shopify:section:unload", () =>
            requestAnimationFrame(() => this.observeHeights())
        )

        if (this.transparentHeader && this.transparentTemplate) {
            this.closest(".shopify-section").classList.add("h-0")
        }

        if (
            this.stickyBehavior === "always" ||
            this.stickyBehavior === "scroll_up"
        ) {
            window.addEventListener("scroll", this.onScroll.bind(this), false)
        }
    }

    observeHeights() {
        // Every fixed bar above the header (announcement bar, countdown timer)
        this.announcementBars = document.querySelectorAll(".announcement-bar")

        this.resizeObserver?.disconnect()
        this.resizeObserver = new ResizeObserver(() => this.updateHeightVars())
        this.resizeObserver.observe(this.header)
        this.announcementBars.forEach((bar) => this.resizeObserver.observe(bar))

        this.updateHeightVars()
    }

    updateHeightVars() {
        this.headerHeight = this.header.offsetHeight
        // Stack the bars in DOM order. Unrounded, so each sits flush under the previous bar's 0.5px border
        this.announcementBarHeight = 0
        this.announcementBars.forEach((bar) => {
            bar.style.top = `${this.announcementBarHeight}px`
            this.announcementBarHeight += bar.getBoundingClientRect().height
        })

        document.documentElement.style.setProperty(
            "--header-height",
            `${this.headerHeight}px`
        )
        document.documentElement.style.setProperty(
            "--announcement-bar-height",
            `${this.announcementBarHeight}px`
        )
    }

    onScroll() {
        if (!this.ticking) {
            window.requestAnimationFrame(() => {
                this.updateHeaderVisibility()
                this.ticking = false
            })
            this.ticking = true
        }
    }

    toggleMegamenu(megamenu, close = false) {
        if (megamenu && !megamenu.classList.contains("active") && !close) {
            this.classList.add("nav-transition")
            this.classList.add("show-bg")
            this.megamenus.forEach((menu) => menu.classList.remove("active"))
            this.resetMegamenuTabs(megamenu)
            megamenu.classList.add("active")
            window.menuOpen = true
            lenis.stop()
        } else if (megamenu) {
            if (this.classList.contains("initial")) {
                this.classList.remove("show-bg")
            }
            this.classList.remove("nav-transition")
            megamenu.classList.remove("active")
            window.menuOpen = false
            lenis.start()
        }

        this.syncMobileTriggerLabels()
    }

    syncMobileTriggerLabels() {
        if (window.innerWidth >= 1024) return

        const anyOpen = Array.from(this.megamenus).some((menu) =>
            menu.classList.contains("active")
        )

        document
            .querySelectorAll("[megamenu-link][mobile-label]")
            .forEach((trigger) => {
                const label = anyOpen
                    ? trigger.getAttribute("mobile-label")
                    : trigger.getAttribute("original-label")
                if (label && trigger.textContent.trim() !== label) {
                    trigger.textContent = label
                }
            })
    }

    setupMegamenu() {
        const links = document.querySelectorAll("[megamenu-link]")

        const menu = document.querySelector(".megamenu")
        const trigger = document.querySelector(".megamenu-trigger")

        document.addEventListener("click", (e) => {
            let flag = false

            this.megamenus.forEach((menu) => {
                const isLink = Array.from(links).some((link) =>
                    link.contains(e.target)
                )
                const onMenu = Array.from(this.megamenus).some((el) =>
                    el.contains(e.target)
                )
                if (!onMenu && !isLink) {
                    this.toggleMegamenu(menu, true)
                }
            })
        })

        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                this.megamenus.forEach((menu) => {
                    this.toggleMegamenu(menu, true)
                })
            }
        })

        links.forEach((link) => {
            const megamenu = document.querySelector(
                `.${link.getAttribute("megamenu-link")}`
            )

            link.addEventListener("click", () => {
                if (
                    window.innerWidth < 1024 &&
                    link.hasAttribute("mobile-label")
                ) {
                    if (
                        megamenu.classList.contains("active") ||
                        Array.from(this.megamenus).some((e) =>
                            e.classList.contains("active")
                        )
                    ) {
                        this.megamenus.forEach((menu) => {
                            this.toggleMegamenu(menu, true)
                        })
                    } else if (!megamenu.classList.contains("active")) {
                        this.toggleMegamenu(megamenu)
                    }
                } else {
                    this.toggleMegamenu(megamenu)
                }
            })
        })

        const tabs = document.querySelectorAll("[tab]")

        tabs.forEach((tab) => {
            const scope = tab.closest("megamenu") || document

            const activate = () => {
                scope
                    .querySelectorAll("[megamenu-content]")
                    .forEach((menu) => menu.classList.remove("active"))
                scope
                    .querySelectorAll("[tab]")
                    .forEach((tab) => tab.classList.remove("active"))
                const tabTrigger = tab.getAttribute("tab")
                tab.classList.add("active")
                scope.querySelector(`.${tabTrigger}`)?.classList.add("active")
            }

            tab.addEventListener("mouseenter", activate)
            tab.addEventListener("click", activate)
        })
    }

    resetMegamenuTabs(megamenu) {
        const tabs = megamenu.querySelectorAll("[tab]")
        if (!tabs.length) return

        megamenu
            .querySelectorAll("[megamenu-content]")
            .forEach((menu) => menu.classList.remove("active"))
        tabs.forEach((tab) => tab.classList.remove("active"))

        const firstTab = tabs[0]
        firstTab.classList.add("active")
        megamenu
            .querySelector(`.${firstTab.getAttribute("tab")}`)
            ?.classList.add("active")
    }

    updateHeaderVisibility() {
        const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop
        const headerBottom = this.header.getBoundingClientRect().bottom

        // The announcement bar is fixed, so the header pins right under it
        if (scrollTop > 0 && this.stickyBehavior === "always") {
            this.header.classList.add("fixed", "top-0")
            this.header.style.transform = "translateY(0)"
            this.header.classList.remove("initial")
            if (this.transparentHeader && this.transparentTemplate) {
                this.classList.add("show-bg")
            }
        } else if (scrollTop < 10) {
            if (this.transparentTemplate && this.transparentHeader) {
                this.header.classList.remove("show-bg", "fixed")
                this.header.classList.add("initial")
            }
        }

        if (this.stickyBehavior === "scroll_up") {
            if (
                scrollTop < this.lastScrollTop &&
                headerBottom <= this.announcementBarHeight
            ) {
                this.header.classList.add("fixed", "top-0")
                this.header.style.transform = "translateY(0)"
                if (this.transparentTemplate && this.transparentHeader) {
                    this.header.classList.add("show-bg")
                    this.header.classList.remove("initial")
                }
            } else if (
                scrollTop > this.lastScrollTop &&
                scrollTop > this.headerHeight + this.announcementBarHeight
            ) {
                this.header.style.transform = `translateY(-${this.headerHeight}px)`

                if (this.transparentTemplate && this.transparentHeader)
                    this.header.classList.remove("show-bg")
            } else if (scrollTop < 10) {
                if (this.transparentTemplate && this.transparentHeader) {
                    this.header.classList.remove("show-bg", "fixed")
                    this.header.classList.add("initial")
                }
            }
        }

        this.lastScrollTop = scrollTop
    }

    setupDesktopSubmenuBehavior() {
        const menuItems = this.header.querySelectorAll("nav > ul > li")

        menuItems.forEach((item) => {
            const submenu =
                item.querySelector("ul") || item.querySelector(".mega-menu")
            if (submenu) {
                if (this.submenuAction === "hover") {
                    item.addEventListener("mouseenter", () => {
                        item.classList.add("active-menu-link")
                    })
                    item.addEventListener("mouseleave", () => {
                        item.classList.remove("active-menu-link")
                    })
                } else if (this.submenuAction === "click") {
                    item.querySelector("a").addEventListener("click", (e) => {
                        e.preventDefault()
                        item.classList.toggle("active-menu-link")
                        menuItems.forEach((otherItem) => {
                            if (otherItem !== item) {
                                otherItem.classList.remove("active-menu-link")
                            }
                        })
                    })

                    document.addEventListener("click", (e) => {
                        if (!item.contains(e.target)) {
                            item.classList.remove("active-menu-link")
                        }
                    })
                }
            }
        })
    }

    setupMenuDrawer() {
        const menuToggle = this.header.querySelector("#menu-toggle")
        const menuDrawer = this.header.querySelector("#menu-drawer")
        const menuIcon = menuToggle.querySelector(".menu-icon")
        const closeIcon = menuToggle.querySelector(".close-icon")
        const overlay = this.header.querySelector("#drawer-overlay")

        this.setDrawerHeight()
        window.addEventListener("resize", this.setDrawerHeight.bind(this))
        window.addEventListener("scroll", this.setDrawerHeight.bind(this))

        menuToggle.addEventListener("click", () => {
            this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay)
        })

        overlay.addEventListener("click", () => {
            this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay)
        })

        if (this.mobileMenuType !== "accordion") {
            this.setupNewPageInDrawer(menuDrawer)
        }
    }

    setDrawerHeight() {
        const headerHeight = this.header.offsetHeight
        let announcementBarVisibleHeight = 0

        document.querySelectorAll(".announcement-bar").forEach((bar) => {
            announcementBarVisibleHeight = Math.max(
                announcementBarVisibleHeight,
                bar.getBoundingClientRect().bottom
            )
        })

        const totalHeight = headerHeight + announcementBarVisibleHeight
        const menuDrawer = this.header.querySelector("#menu-drawer")
        const menuDrawerOverlay = this.header.querySelector("#drawer-overlay")
        menuDrawer.style.height = `calc(100dvh - ${totalHeight}px)`
        menuDrawer.style.top = `${totalHeight}px`
        menuDrawerOverlay.style.height = `calc(100dvh - ${totalHeight}px)`
        menuDrawerOverlay.style.top = `${totalHeight}px`
    }

    toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay) {
        menuDrawer.classList.toggle("translate-x-full")
        menuIcon.classList.toggle("hidden")
        closeIcon.classList.toggle("hidden")
        overlay.classList.toggle("hidden")
    }

    setupNewPageInDrawer(menuDrawer) {
        const submenuTriggers = menuDrawer.querySelectorAll(".submenu-trigger")
        submenuTriggers.forEach((trigger) => {
            trigger.addEventListener("click", (e) => {
                e.preventDefault()
                const submenuId = e.target.dataset.submenu
                const submenu = this.header.querySelector(
                    `#submenu-${submenuId}`
                )
                submenu.classList.remove("hidden")
            })
        })

        const submenuBackButtons = menuDrawer.querySelectorAll(".submenu-back")
        submenuBackButtons.forEach((button) => {
            button.addEventListener("click", (e) => {
                e.target.closest(".submenu").classList.add("hidden")
            })
        })
    }

    setupPredictiveSearch() {
        const predictiveSearch = document.querySelector("#predictive-search")
        const resultsWrapper = document.querySelector("#search-results-wrapper")
        const searchInput = document.querySelector("#search-input")
        const searchResults = document.querySelector("#search-results")
        const searchClear = document.querySelector("#search-clear")
        const searchContainer = document.querySelector(".search-form")
        const featuredSearches = document.querySelectorAll(
            "[data-featured-search]"
        )

        featuredSearches.forEach((item) => {
            item.addEventListener("click", () => {
                searchInput.value = item.textContent.trim()
                const inputEvent = new Event("input", { bubbles: true })
                searchInput.dispatchEvent(inputEvent)
            })
        })

        if (searchContainer) {
            this.searchContainerHeight = searchContainer
                ? searchContainer.offsetHeight
                : 0
            document.documentElement.style.setProperty(
                "--search-container-height",
                `${this.searchContainerHeight}px`
            )
        }

        const updateSearchPosition = () => {
            const headerRect = this.header.getBoundingClientRect()
            const visibleHeight =
                headerRect.height + Math.max(0, headerRect.top)

            predictiveSearch.style.top = `${visibleHeight}px`
            searchOverlay.style.top = `${visibleHeight}px`
            searchOverlay.style.height = `calc(100dvh - ${visibleHeight}px)`
        }

        searchClear?.addEventListener("click", () => {
            searchInput.value = ""
            searchResults.innerHTML = ""
            searchClear.classList.add("hidden")
            searchInput.focus()
            this.toggleMegamenu(document.querySelector("megamenu.search"))
        })

        const debounce = (func, wait) => {
            let timeout
            return (...args) => {
                clearTimeout(timeout)
                timeout = setTimeout(() => func.apply(this, args), wait)
            }
        }

        searchInput.addEventListener(
            "input",
            debounce(async (e) => {
                const query = e.target.value.trim()

                if (query.length > 2) {
                    searchClear?.classList.remove("hidden")

                    try {
                        const response = await fetch(
                            `/search/suggest?q=${query}&section_id=predictive-search`
                        )
                        // const response1 = await fetch(`/search/suggest.json?q=${query}`);
                        // const data = await response1.json();
                        // console.log(data)
                        if (!response.ok) {
                            throw new Error(
                                `HTTP error! status: ${response.status}`
                            )
                        }

                        const text = await response.text()
                        const parser = new DOMParser()
                        const parsedDocument = parser.parseFromString(
                            text,
                            "text/html"
                        )

                        searchResults.parentElement.classList.remove("hidden")
                        searchResults.parentElement.classList.add("flex")

                        const resultsMarkup =
                            parsedDocument.querySelector(
                                "#predictive-search-results"
                            )?.innerHTML || ""
                        searchResults.innerHTML = resultsMarkup
                    } catch (error) {
                        console.error("Error fetching search results:", error)
                    }
                } else {
                    searchResults.innerHTML = ""
                    searchClear.classList.add("hidden")
                }
            }, 300)
        )
    }
}

customElements.define("header-487", ShopifyHeader)
